import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { doc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import ClaireoaksLogo from '../components/ClaireoaksLogo';

const { useState } = React;

export default function SignupPage() {
  const { signup } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    personalInfo: {
      firstName: '',
      lastName: '',
      dateOfBirth: '',
      nationality: '',
      maritalStatus: 'Single',
      address: {
        street: '',
        city: '',
        county: '',
        country: '',
        postCode: ''
      }
    },
    contactDetails: {
      phoneNumber: '',
      alternativeEmail: '',
      emergencyContact: {
        name: '',
        relationship: '',
        phone: ''
      }
    },
    documents: {
      idCardType: 'Passport', // Default value
      idCardNumber: '',
      idCardUrl: '' // Will be populated after file upload
    }
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Handle nested fields
    const fields = name.split('.');
    
    setFormData(prev => {
      let newFormData = { ...prev };
      
      if (fields.length === 1) {
        // Handle top-level fields
        newFormData[fields[0]] = value;
      } else if (fields.length === 2) {
        // Handle two-level nested fields (e.g., personalInfo.firstName)
        if (fields[0] === 'emergency') {
          // Special case for emergency contact
          newFormData.contactDetails = {
            ...newFormData.contactDetails,
            emergencyContact: {
              ...newFormData.contactDetails.emergencyContact,
              [fields[1]]: value
            }
          };
        } else {
          newFormData[fields[0]] = {
            ...newFormData[fields[0]],
            [fields[1]]: value
          };
        }
      } else if (fields.length === 3) {
        // Handle three-level nested fields (e.g., personalInfo.address.street)
        newFormData[fields[0]] = {
          ...newFormData[fields[0]],
          [fields[1]]: {
            ...newFormData[fields[0]][fields[1]],
            [fields[2]]: value
          }
        };
      }
      
      return newFormData;
    });
  };

  async function handleSubmit(e) {
    e.preventDefault();

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      return setError('Please enter a valid email address');
    }

    if (formData.password !== formData.confirmPassword) {
      return setError('Passwords do not match');
    }

    try {
      setError('');
      setLoading(true);
      
      // Create the user in Firebase Auth
      const userCredential = await signup(formData.email.trim(), formData.password);
      
      // Create the user document in Firestore
      await setDoc(doc(db, 'users', userCredential.user.uid), {
        email: formData.email.trim(),
        personalInfo: formData.personalInfo,
        contactDetails: formData.contactDetails,
        documents: {
          ...formData.documents,
          verificationStatus: 'pending'
        },
        role: 'pending',
        status: 'pending',
        registrationDate: new Date().toISOString(),
        createdAt: new Date().toISOString(),
        showMockTransactions: false
      });

      // Navigate to success page
      navigate('/registration-success');
    } catch (error) {
      console.error('Signup error:', error);
      // More specific error messages
      if (error.code === 'auth/invalid-email') {
        setError('Invalid email format. Please check your email address.');
      } else if (error.code === 'auth/email-already-in-use') {
        setError('An account with this email already exists.');
      } else if (error.code === 'auth/weak-password') {
        setError('Password should be at least 6 characters long.');
      } else {
        setError('Failed to create an account. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-mist">
      <div className="flex min-h-screen flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="flex justify-center">
            <ClaireoaksLogo />
          </div>
          <h2 className="mt-6 text-center font-display text-3xl font-semibold text-oak-900">
            Create your account
          </h2>
          <p className="mt-2 text-center text-sm text-oak-600">
            Join Claireoaks Credit Union
          </p>
        </div>

        <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-2xl">
          <div className="border border-oak-100 bg-white px-4 py-8 shadow-soft sm:px-10">
            {error && (
              <div className="mb-4 bg-red-50 border-l-4 border-red-400 p-4 rounded-lg">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Account Information */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900">Account Information</h3>
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                      Email address
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className="mt-1 appearance-none block w-full px-4 py-3 rounded-xl border 
                        border-gray-200 focus:ring-2 focus:ring-[#123524]/20 focus:border-[#123524] 
                        transition-colors"
                    />
                  </div>
                  <div>
                    <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                      Password
                    </label>
                    <input
                      id="password"
                      name="password"
                      type="password"
                      required
                      value={formData.password}
                      onChange={handleChange}
                      className="mt-1 appearance-none block w-full px-4 py-3 rounded-xl border 
                        border-gray-200 focus:ring-2 focus:ring-[#123524]/20 focus:border-[#123524] 
                        transition-colors"
                    />
                  </div>
                  <div>
                    <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                      Confirm Password
                    </label>
                    <input
                      id="confirmPassword"
                      name="confirmPassword"
                      type="password"
                      required
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      className="mt-1 appearance-none block w-full px-4 py-3 rounded-xl border 
                        border-gray-200 focus:ring-2 focus:ring-[#123524]/20 focus:border-[#123524] 
                        transition-colors"
                    />
                  </div>
                </div>
              </div>

              {/* Personal Information */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900">Personal Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="personalInfo.firstName" className="block text-sm font-medium text-gray-700">
                      First Name
                    </label>
                    <input
                      id="personalInfo.firstName"
                      name="personalInfo.firstName"
                      type="text"
                      required
                      value={formData.personalInfo.firstName}
                      onChange={handleChange}
                      className="mt-1 appearance-none block w-full px-4 py-3 rounded-xl border 
                        border-gray-200 focus:ring-2 focus:ring-[#123524]/20 focus:border-[#123524] 
                        transition-colors"
                    />
                  </div>
                  <div>
                    <label htmlFor="personalInfo.lastName" className="block text-sm font-medium text-gray-700">
                      Last Name
                    </label>
                    <input
                      id="personalInfo.lastName"
                      name="personalInfo.lastName"
                      type="text"
                      required
                      value={formData.personalInfo.lastName}
                      onChange={handleChange}
                      className="mt-1 appearance-none block w-full px-4 py-3 rounded-xl border 
                        border-gray-200 focus:ring-2 focus:ring-[#123524]/20 focus:border-[#123524] 
                        transition-colors"
                    />
                  </div>
                  <div>
                    <label htmlFor="personalInfo.dateOfBirth" className="block text-sm font-medium text-gray-700">
                      Date of Birth
                    </label>
                    <input
                      id="personalInfo.dateOfBirth"
                      name="personalInfo.dateOfBirth"
                      type="date"
                      required
                      value={formData.personalInfo.dateOfBirth}
                      onChange={handleChange}
                      className="mt-1 appearance-none block w-full px-4 py-3 rounded-xl border 
                        border-gray-200 focus:ring-2 focus:ring-[#123524]/20 focus:border-[#123524] 
                        transition-colors"
                    />
                  </div>
                  <div>
                    <label htmlFor="personalInfo.nationality" className="block text-sm font-medium text-gray-700">
                      Nationality
                    </label>
                    <input
                      id="personalInfo.nationality"
                      name="personalInfo.nationality"
                      type="text"
                      required
                      value={formData.personalInfo.nationality}
                      onChange={handleChange}
                      className="mt-1 appearance-none block w-full px-4 py-3 rounded-xl border 
                        border-gray-200 focus:ring-2 focus:ring-[#123524]/20 focus:border-[#123524] 
                        transition-colors"
                    />
                  </div>
                  <div>
                    <label htmlFor="personalInfo.maritalStatus" className="block text-sm font-medium text-gray-700">
                      Marital Status
                    </label>
                    <select
                      id="personalInfo.maritalStatus"
                      name="personalInfo.maritalStatus"
                      required
                      value={formData.personalInfo.maritalStatus}
                      onChange={handleChange}
                      className="mt-1 appearance-none block w-full px-4 py-3 rounded-xl border 
                        border-gray-200 focus:ring-2 focus:ring-[#123524]/20 focus:border-[#123524] 
                        transition-colors"
                    >
                      <option value="Single">Single</option>
                      <option value="Married">Married</option>
                      <option value="Divorced">Divorced</option>
                      <option value="Widowed">Widowed</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Address Information */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900">Address Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <label htmlFor="personalInfo.address.street" className="block text-sm font-medium text-gray-700">
                      Street Address
                    </label>
                    <input
                      id="personalInfo.address.street"
                      name="personalInfo.address.street"
                      type="text"
                      required
                      value={formData.personalInfo.address.street}
                      onChange={handleChange}
                      className="mt-1 appearance-none block w-full px-4 py-3 rounded-xl border 
                        border-gray-200 focus:ring-2 focus:ring-[#123524]/20 focus:border-[#123524] 
                        transition-colors"
                    />
                  </div>
                  <div>
                    <label htmlFor="personalInfo.address.city" className="block text-sm font-medium text-gray-700">
                      City
                    </label>
                    <input
                      id="personalInfo.address.city"
                      name="personalInfo.address.city"
                      type="text"
                      required
                      value={formData.personalInfo.address.city}
                      onChange={handleChange}
                      className="mt-1 appearance-none block w-full px-4 py-3 rounded-xl border 
                        border-gray-200 focus:ring-2 focus:ring-[#123524]/20 focus:border-[#123524] 
                        transition-colors"
                    />
                  </div>
                  <div>
                    <label htmlFor="personalInfo.address.county" className="block text-sm font-medium text-gray-700">
                      State
                    </label>
                    <input
                      id="personalInfo.address.county"
                      name="personalInfo.address.county"
                      type="text"
                      value={formData.personalInfo.address.county}
                      onChange={handleChange}
                      className="mt-1 appearance-none block w-full px-4 py-3 rounded-xl border 
                        border-gray-200 focus:ring-2 focus:ring-[#123524]/20 focus:border-[#123524] 
                        transition-colors"
                    />
                  </div>
                  <div>
                    <label htmlFor="personalInfo.address.country" className="block text-sm font-medium text-gray-700">
                      Country
                    </label>
                    <input
                      id="personalInfo.address.country"
                      name="personalInfo.address.country"
                      type="text"
                      required
                      value={formData.personalInfo.address.country}
                      onChange={handleChange}
                      className="mt-1 appearance-none block w-full px-4 py-3 rounded-xl border 
                        border-gray-200 focus:ring-2 focus:ring-[#123524]/20 focus:border-[#123524] 
                        transition-colors"
                    />
                  </div>
                  <div>
                    <label htmlFor="personalInfo.address.postCode" className="block text-sm font-medium text-gray-700">
                      ZIP code
                    </label>
                    <input
                      id="personalInfo.address.postCode"
                      name="personalInfo.address.postCode"
                      type="text"
                      required
                      value={formData.personalInfo.address.postCode}
                      onChange={handleChange}
                      placeholder="e.g. SW1A 1AA"
                      className="mt-1 appearance-none block w-full px-4 py-3 rounded-xl border 
                        border-gray-200 focus:ring-2 focus:ring-[#123524]/20 focus:border-[#123524] 
                        transition-colors"
                    />
                  </div>
                </div>
              </div>

              {/* Contact Information */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900">Contact Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="contactDetails.phoneNumber" className="block text-sm font-medium text-gray-700">
                      Phone Number
                    </label>
                    <input
                      id="contactDetails.phoneNumber"
                      name="contactDetails.phoneNumber"
                      type="tel"
                      required
                      value={formData.contactDetails.phoneNumber}
                      onChange={handleChange}
                      className="mt-1 appearance-none block w-full px-4 py-3 rounded-xl border 
                        border-gray-200 focus:ring-2 focus:ring-[#123524]/20 focus:border-[#123524] 
                        transition-colors"
                    />
                  </div>
                  <div>
                    <label htmlFor="contactDetails.alternativeEmail" className="block text-sm font-medium text-gray-700">
                      Alternative Email
                    </label>
                    <input
                      id="contactDetails.alternativeEmail"
                      name="contactDetails.alternativeEmail"
                      type="email"
                      value={formData.contactDetails.alternativeEmail}
                      onChange={handleChange}
                      className="mt-1 appearance-none block w-full px-4 py-3 rounded-xl border 
                        border-gray-200 focus:ring-2 focus:ring-[#123524]/20 focus:border-[#123524] 
                        transition-colors"
                    />
                  </div>
                </div>
              </div>

              {/* Emergency Contact */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900">Emergency Contact</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="emergency.name" className="block text-sm font-medium text-gray-700">
                      Contact Name
                    </label>
                    <input
                      id="emergency.name"
                      name="emergency.name"
                      type="text"
                      required
                      value={formData.contactDetails.emergencyContact.name}
                      onChange={handleChange}
                      className="mt-1 appearance-none block w-full px-4 py-3 rounded-xl border 
                        border-gray-200 focus:ring-2 focus:ring-[#123524]/20 focus:border-[#123524] 
                        transition-colors"
                    />
                  </div>
                  <div>
                    <label htmlFor="emergency.relationship" className="block text-sm font-medium text-gray-700">
                      Relationship
                    </label>
                    <input
                      id="emergency.relationship"
                      name="emergency.relationship"
                      type="text"
                      required
                      value={formData.contactDetails.emergencyContact.relationship}
                      onChange={handleChange}
                      className="mt-1 appearance-none block w-full px-4 py-3 rounded-xl border 
                        border-gray-200 focus:ring-2 focus:ring-[#123524]/20 focus:border-[#123524] 
                        transition-colors"
                    />
                  </div>
                  <div>
                    <label htmlFor="emergency.phone" className="block text-sm font-medium text-gray-700">
                      Contact Phone
                    </label>
                    <input
                      id="emergency.phone"
                      name="emergency.phone"
                      type="tel"
                      required
                      value={formData.contactDetails.emergencyContact.phone}
                      onChange={handleChange}
                      className="mt-1 appearance-none block w-full px-4 py-3 rounded-xl border 
                        border-gray-200 focus:ring-2 focus:ring-[#123524]/20 focus:border-[#123524] 
                        transition-colors"
                    />
                  </div>
                </div>
              </div>

              {/* ID Document */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900">Identification Document</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="documents.idCardType" className="block text-sm font-medium text-gray-700">
                      ID Type
                    </label>
                    <select
                      id="documents.idCardType"
                      name="documents.idCardType"
                      required
                      value={formData.documents.idCardType}
                      onChange={handleChange}
                      className="mt-1 appearance-none block w-full px-4 py-3 rounded-xl border 
                        border-gray-200 focus:ring-2 focus:ring-[#123524]/20 focus:border-[#123524] 
                        transition-colors"
                    >
                      <option value="Passport">Passport</option>
                      <option value="National ID">National ID</option>
                      <option value="Driver's License">Driver's License</option>
                    </select>
                  </div>
                  <div>
                    <label htmlFor="documents.idCardNumber" className="block text-sm font-medium text-gray-700">
                      ID Number
                    </label>
                    <input
                      id="documents.idCardNumber"
                      name="documents.idCardNumber"
                      type="text"
                      required
                      value={formData.documents.idCardNumber}
                      onChange={handleChange}
                      className="mt-1 appearance-none block w-full px-4 py-3 rounded-xl border 
                        border-gray-200 focus:ring-2 focus:ring-[#123524]/20 focus:border-[#123524] 
                        transition-colors"
                    />
                  </div>
                </div>
              </div>

              <div>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full flex justify-center py-3 px-4 border border-transparent 
                    rounded-xl shadow-sm text-sm font-medium text-white bg-[#123524] 
                    hover:bg-[#0d2318] focus:outline-none focus:ring-2 focus:ring-offset-2 
                    focus:ring-[#123524]/20 transition-colors"
                >
                  {loading ? 'Creating account...' : 'Create account'}
                </button>
              </div>
            </form>

            <div className="mt-6 text-center">
              <p className="text-sm text-gray-600">
                Already have an account?{' '}
                <a href="/login" className="font-medium text-[#123524] hover:text-[#0d2318]">
                  Sign in
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
} 